<script>
import BarChart from "@/components/bar-chart.vue";
import PieChart from "@/components/pie-chart.vue";
import { toCurrency } from "@/helpers/toCurrency";
import DisplayValueBlock from "./shared/display-value-block.vue";
import ProjetoFiltersModal, {
  createEmptyFilters
} from "./shared/projeto-filters-modal.vue";
import TrafficLight from "./shared/traffic-light.vue";
import { ChartColorsEnum } from "./constants";
import { isNil } from "lodash";
import moment from "moment";
import { mapGetters } from "vuex";

/**
 * @typedef {Object} Orcamento
 * @property {number} total - Valor total do orçamento
 * @property {number} percentual - Percentual realizado (%)
 * @property {number} projetado - Valor projetado (R$)
 * @property {number} realizado - Valor realizado (R$)
 */

/**
 * @typedef {Object} HorasDesenvolvimento
 * @property {number} percentual - Percentual realizado (%)
 * @property {number} projetado - Horas projetadas
 * @property {number} realizado - Horas realizadas
 */

/**
 * @typedef {Object} OrcamentoMensal
 * @property {string} competencia
 * @property {number} projetado
 * @property {number} realizado
 */

/**
 * @typedef {Object} DashboardProjeto
 * @property {Orcamento} orcamento
 * @property {HorasDesenvolvimento} horas_desenvolvimento
 * @property {number} orcamento_estimado
 * @property {number} orcamento_realizado
 * @property {number} participantes
 * @property {number} vagas
 * @property {number} retorno_esperado
 * @property {string} escopo
 * @property {string} data_inicio_previsto
 * @property {string} data_inicio_revisada
 * @property {string} data_fim_previsto
 * @property {string} data_fim_revisada
 * @property {number} vpl_inicial
 * @property {number} vpl_revisado
 * @property {number} orcamento_inicial
 * @property {number} orcamento_revisado
 * @property {OrcamentoMensal[]} orcamento_mensal
 */

/**
 * @function createEmptyData
 * @returns {DashboardProjeto}
 */
const createEmptyData = () => ({
  data_fim_previsto: "",
  data_fim_revisada: "",
  data_inicio_previsto: "",
  data_inicio_revisada: "",
  escopo: "",
  horas_desenvolvimento: {
    projetado: 0,
    realizado: 0,
    percentual: 0
  },
  nome_iniciativa: "",
  orcamento: {
    percentual: 0,
    projetado: 0,
    realizado: 0,
    total: 0
  },
  orcamento_estimado: 0,
  orcamento_inicial: 0,
  orcamento_mensal: [],
  orcamento_realizado: 0,
  orcamento_revisado: 0,
  participantes: 0,
  retorno_esperado: 0,
  vagas: 0,
  vpl_inicial: 0,
  vpl_revisado: 0
});

const parseNumber = number =>
  parseFloat(number).toLocaleString("pt-br", {
    maximumFractionDigits: 2
  });

const parseHours = number => {
  const hour = Math.floor(number);
  const minutes = Math.floor((number - hour) * 60 * 100);
  return (
    hour.toString().padStart(2, "0") + ":" + minutes.toString().padStart(2, "0")
  );
};

export default {
  components: {
    BarChart,
    DisplayValueBlock,
    ProjetoFiltersModal,
    PieChart,
    TrafficLight
  },
  data() {
    return {
      beginYear: 0,
      endYear: 0,
      data: createEmptyData(),
      filters: createEmptyFilters()
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    horasDesenvolvimento() {
      const {
        percentual,
        projetado,
        realizado
      } = this.data.horas_desenvolvimento;
      const chartdata = {
        labels: [`${parseHours(realizado)} - ${parseNumber(percentual)}%`],
        datasets: [
          {
            backgroundColor: [ChartColorsEnum.darkBlue_25, "transparent"],
            borderColor: [ChartColorsEnum.darkBlue_25, "black"],
            borderWidth: 1,
            data:
              realizado >= 100
                ? [realizado]
                : [realizado, projetado - realizado],
            label: "Pizza"
          }
        ]
      };
      const options = {
        cutoutPercentage: 50,
        circumference: Math.PI,
        rotation: Math.PI,
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "bottom"
        },
        tooltips: {
          callbacks: {
            label: tooltipItem =>
              tooltipItem.index > 0
                ? `Projetado: ${parseHours(projetado)}`
                : `Realizado: ${parseHours(realizado)} - ${percentual}%`
          }
        }
      };

      return { chartdata, options };
    },
    orcamentoMensal() {
      const isMonthly = !isNil(this.filters.ano_base);
      let orcamento = this.data?.orcamento_mensal;

      orcamento = orcamento?.map(
        ({ ano, competencia, projetado, realizado }) => ({
          label: isMonthly
            ? moment(competencia, "YYYY-MM").format("MM/YYYY")
            : ano?.toString(),
          projetado: projetado ? parseFloat(projetado) : 0,
          realizado: realizado ? parseFloat(realizado) : 0
        })
      );

      const total = orcamento.reduce(
        (acc, { projetado, realizado }) => ({
          ...acc,
          projetado: acc.projetado + projetado,
          realizado: acc.realizado + realizado
        }),
        {
          label: "Total",
          projetado: 0,
          realizado: 0
        }
      );
      orcamento
        .sort(({ label: a }, { label: b }) => a.localeCompare(b))
        .push(total);

      const chartdata = {
        labels: orcamento.map(({ label }) => label),
        datasets: [
          {
            label: "Budget",
            stack: "stacked",
            data: orcamento.map(({ projetado }) => projetado),
            backgroundColor: ChartColorsEnum.darkestBlue,
            borderColor: ChartColorsEnum.darkestBlue,
            borderWidth: 1
          },
          {
            label: "Real + Forecast",
            stack: "stacked",
            data: orcamento.map(({ realizado }) => realizado),
            backgroundColor: ChartColorsEnum.darkBlue_25,
            borderColor: ChartColorsEnum.darkBlue_25,
            borderWidth: 1
          }
        ]
      };
      const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              stacked: true
            }
          ],
          yAxes: [
            {
              ticks: {
                callback(value) {
                  return toCurrency(value)
                    .replace("R$", "")
                    .trim();
                }
              }
            }
          ]
        },
        legend: {
          position: "bottom"
        },
        tooltips: {
          mode: "index",
          callbacks: {
            label(tooltipItem, data) {
              const ref = data.datasets[tooltipItem.datasetIndex];
              let label = ref.label || "";

              if (label) {
                label += ": ";
              }

              if (tooltipItem?.value !== null) {
                label += new Intl.NumberFormat("pt-BR").format(
                  tooltipItem.value
                );
              }

              return label;
            },
            afterBody(tooltipItem) {
              if (tooltipItem?.length > 1) {
                const total = tooltipItem.reduce(
                  (sum, { value }) => sum + value * 1,
                  0
                );
                return [
                  "Total: " + new Intl.NumberFormat("pt-BR").format(total)
                ];
              }
            }
          }
        }
      };

      return { chartdata, options };
    },
    orcamento() {
      const { percentual, projetado, realizado } = this.data.orcamento;
      const chartdata = {
        labels: [`${toCurrency(realizado)} - ${parseNumber(percentual)}%`],
        datasets: [
          {
            backgroundColor: [ChartColorsEnum.darkBlue_25, "transparent"],
            borderColor: [ChartColorsEnum.darkBlue_25, "black"],
            borderWidth: 1,
            data:
              percentual >= 100
                ? [realizado]
                : [realizado, projetado - realizado],
            label: "Pizza"
          }
        ]
      };
      const options = {
        cutoutPercentage: 50,
        circumference: Math.PI,
        rotation: Math.PI,
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: "bottom"
        },
        tooltips: {
          callbacks: {
            label: tooltipItem =>
              tooltipItem.index > 0
                ? `Projetado: ${toCurrency(projetado)}`
                : `Realizado: ${toCurrency(realizado)} - ${percentual}%`
          }
        }
      };

      return { chartdata, options };
    },
    statusOrcamento() {
      switch (this.data.orcamento_status) {
        case "offset":
          return {
            color: "red",
            tooltip: "Offset"
          };
        case "saving":
          return {
            color: "yellow",
            tooltip: "Saving"
          };
        default:
          return {
            color: "green",
            tooltip: "Em linha"
          };
      }
    },
    statusPrazo() {
      switch (this.data.data_status) {
        case "em_atraso":
          return {
            color: "red",
            tooltip: "Em atraso"
          };
        case "adiantado":
          return {
            color: "yellow",
            tooltip: "Adiantado"
          };
        default:
          return {
            color: "green",
            tooltip: "No prazo"
          };
      }
    },
    statusVPL() {
      if (this.data.vpl_status === "negativo") {
        return {
          color: "red",
          tooltip: "Negativo"
        };
      }

      return {
        color: "green",
        tooltip: "Positivo"
      };
    },
    yearsOpts() {
      const length = Math.abs(this.endYear - this.beginYear) + 1;

      if (length === 1) {
        return [];
      }

      return Array.from({ length }, (_, index) => {
        const value = Math.min(this.beginYear, this.endYear) + index;

        return {
          label: value.toString(),
          value
        };
      });
    }
  },
  created() {
    if (this.clientId === 0) {
      this.goToHome();
    } else {
      this.doLoad();
    }
  },
  watch: {
    clientId(value) {
      if (value === 0) {
        this.goToHome();
      } else {
        this.doLoad();
      }
    }
  },
  methods: {
    /**
     * @function doLoad
     * @param {any} filters
     * @returns {Promise<DashboardProjeto>}
     */
    async doLoad(filters) {
      try {
        if (filters) {
          this.filters = filters;
        }

        const { ano_base, business_plan_id } = this.filters;

        if (!business_plan_id) {
          return;
        }

        let query = ano_base ? `?filters[ano_base]=${ano_base}` : "";

        const { get } = this.apiResource(
          `v1/ativacaoped/${this.clientId}/dashboard/${business_plan_id}${query}`
        );
        const response = await get();
        this.data = response;
      } catch (error) {
        if (!error.isAxiosError) {
          this.notify(error);
        }

        this.data = createEmptyData();
      }
    },
    goToHome() {
      this.notify({
        error:
          "Não é possível acessar o módulo de Ativação P&D sem uma empresa selecionada."
      });
      this.$router.push({ name: "homepage" });
    }
  }
};
</script>

<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" class="d-flex justify-space-between">
        <div>
          <h1>Gestão de projetos - CAPEX</h1>
          <span v-if="filters.business_plan_id">{{
            data.nome_iniciativa || "Business Plan sem nome"
          }}</span>
        </div>
        <div class="d-flex" style="gap: 1rem">
          <v-select
            v-if="filters.business_plan_id && yearsOpts.length"
            v-model="filters.ano_base"
            class="ano-base"
            clearable
            dense
            hide-details
            :items="yearsOpts"
            item-text="label"
            item-value="value"
            label="Ano"
            outlined
            :menu-props="{
              offsetY: true,
              bottom: true
            }"
            @change="doLoad()"
          />

          <ProjetoFiltersModal
            v-model="filters"
            @filter="doLoad"
            @beginYear="beginYear = $event"
            @endYear="endYear = $event"
          >
            <template v-slot:activator="{ on }">
              <v-btn class="pa-5" color="#fff" @click.stop="on.click">
                <v-icon>mdi-filter-variant</v-icon>
                Filtros
              </v-btn>
            </template>
          </ProjetoFiltersModal>
        </div>
      </v-col>

      <v-col v-if="!filters.business_plan_id" cols="12">
        <DisplayValueBlock
          label="Nenhum Business Plan selecionado."
          type="text"
          value="Use o filtro para selecionar um Business Plan e visualizar o relatório."
        />
      </v-col>

      <template v-else>
        <v-col class="d-flex" cols="12" md="6" lg="3">
          <v-card class="px-5">
            <v-card-title>Orçamento</v-card-title>
            <PieChart v-bind="orcamento"></PieChart>
          </v-card>
        </v-col>
        <v-col cols="12" md="6" lg="2" class="display-block-col d-flex">
          <DisplayValueBlock
            label="Orçamento estimado"
            :value="data.orcamento_estimado"
          />
          <DisplayValueBlock
            label="Realizado"
            :value="data.orcamento_realizado"
          />
        </v-col>
        <v-col class="d-flex" cols="12" md="6" lg="3">
          <v-card class="px-5">
            <v-card-title>Horas de desenvolvimento</v-card-title>
            <PieChart v-bind="horasDesenvolvimento"></PieChart>
          </v-card>
        </v-col>
        <v-col cols="12" md="6" lg="4" class="display-block-row-col d-flex">
          <v-row>
            <v-col class="d-flex" cols="12" md="6">
              <DisplayValueBlock
                label="Participantes"
                type="number"
                :value="data.participantes"
              />
            </v-col>
            <v-col class="d-flex" cols="12" md="6">
              <DisplayValueBlock
                label="Vagas"
                type="number"
                :value="data.vagas"
              />
            </v-col>
            <v-col class="d-flex" cols="12">
              <DisplayValueBlock
                label="Retorno esperado"
                :value="data.retorno_esperado"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col class="d-flex" cols="12">
          <DisplayValueBlock label="Escopo" type="text" :value="data.escopo" />
        </v-col>
        <v-col cols="12" md="6" lg="2" class="display-block-col d-flex">
          <DisplayValueBlock
            label="Data início previsto"
            type="date"
            :value="data.data_inicio_previsto"
          />
          <DisplayValueBlock
            label="Data início revisada"
            type="date"
            :value="data.data_inicio_revisada"
          />
        </v-col>
        <v-col cols="12" md="6" lg="2" class="display-block-col d-flex">
          <DisplayValueBlock
            label="Data fim previsto"
            type="date"
            :value="data.data_fim_previsto"
          />
          <DisplayValueBlock
            label="Data fim revisada"
            type="date"
            :value="data.data_fim_revisada"
          />
        </v-col>
        <v-col cols="12" md="6" lg="2" class="display-block-col d-flex">
          <DisplayValueBlock label="VPL inicial" :value="data.vpl_inicial" />
          <DisplayValueBlock label="VPL revisado" :value="data.vpl_revisado" />
        </v-col>
        <v-col cols="12" md="6" lg="2" class="display-block-col d-flex">
          <DisplayValueBlock
            label="Orçamento inicial"
            :value="data.orcamento_inicial"
          />
          <DisplayValueBlock
            label="Orçamento revisado"
            :value="data.orcamento_revisado"
          />
        </v-col>
        <v-col class="d-flex" cols="12" sm="6" md="4">
          <v-card class="px-5 pt-5">
            <BarChart v-bind="orcamentoMensal"></BarChart>
          </v-card>
        </v-col>
        <v-col cols="12" class="status-wrap">
          <TrafficLight v-bind="statusPrazo" label="Status prazo" />
          <TrafficLight v-bind="statusOrcamento" label="Status orçamento" />
          <TrafficLight v-bind="statusVPL" label="Status VPL" />
        </v-col>
      </template>
    </v-row>
  </v-container>
</template>

<style scoped lang="scss">
.v-card {
  flex: 1;
}

.v-card__title {
  word-break: normal !important;
}

.horizontal-bar-chart {
  max-height: 200px;
}

.display-block-col {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.display-block-row-col {
  margin: -0.75rem 0 -0.75rem 0;
}

.status-wrap {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.ano-base::v-deep .v-input__control {
  background-color: #fff;
  width: 125px;
}
</style>
